import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { keyLabel } from '../keymap/platform';

/**
 * A structured footer action for {@link Modal}. When a Modal is given
 * `actions`, it renders the footer buttons itself and wires single-key
 * accelerators, Enter-as-default, the Esc hint, the underlined mnemonic and the
 * typing-target guard — once, so a button can never drift from its key.
 */
export interface ModalAction {
  /** Stable id for keys/tests (optional). */
  id?: string;
  /** Button label; the accelerator letter is underlined where it first appears. */
  label: string;
  onSelect: () => void;
  /** Visual style. */
  variant?: 'default' | 'primary' | 'danger';
  /**
   * Single-key accelerator (case-insensitive). Defaults to the label's first
   * letter. Pass '' to give the action no letter accelerator.
   */
  accelerator?: string;
  /** Enter triggers this action; the button shows a ↵ hint. At most one action. */
  isDefault?: boolean;
  /** Esc triggers this action; the button shows an Esc hint. At most one (the Cancel/dismiss action). */
  isCancel?: boolean;
}

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children?: ReactNode;
  /**
   * Footer actions (buttons). Rendered in a right-aligned action row. Ignored
   * when {@link ModalProps.actions} is provided.
   */
  footer?: ReactNode;
  /**
   * Structured footer actions. When provided, the Modal renders the buttons and
   * wires keyboard accelerators (letter / Enter / Esc) itself, and `footer` is
   * ignored.
   */
  actions?: ModalAction[];
  /** Click the backdrop to dismiss. Default true. */
  closeOnBackdrop?: boolean;
  /** Extra class on the panel (e.g. to widen it for a specific dialog). */
  className?: string;
}

/** CSS selector for the tabbable elements we cycle within the focus trap. */
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The effective single-letter accelerator for an action (default: first letter of label). */
function acceleratorOf(action: ModalAction): string {
  const raw = action.accelerator ?? action.label.charAt(0);
  return raw.toLowerCase();
}

/**
 * True when the active element is a typing target (input/textarea/select or a
 * contentEditable element). Enter and letter accelerators are suppressed in
 * this case so a future dialog with fields keeps native typing intact. Esc is
 * never guarded.
 */
function isTypingTarget(): boolean {
  if (typeof document === 'undefined') return false;
  const el = document.activeElement;
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return el.isContentEditable;
}

/**
 * Render an action label with the accelerator letter underlined via
 * `.mp-btn__accel`. The accelerator's *case* selects which occurrence is
 * underlined:
 *   - An UPPERCASE accelerator (e.g. `'T'`) targets the first EXACT-CASE
 *     (uppercase) occurrence, so "Move to Trash" underlines the **T** in
 *     "Trash" rather than the lowercase "t" in "to". If no uppercase occurrence
 *     exists, it falls back to the case-insensitive match below.
 *   - A lowercase (or any non-uppercase) accelerator keeps the original
 *     case-insensitive first-occurrence behavior.
 * Key matching is unaffected — it is always case-insensitive (see
 * {@link acceleratorOf}); only the visual underline is case-aware. If the
 * letter is not present in the label, the label is rendered plainly (the key
 * still works).
 */
function labelWithAccel(label: string, rawAccel: string): ReactNode {
  let idx = -1;
  if (rawAccel) {
    const isUpper =
      rawAccel !== rawAccel.toLowerCase() && rawAccel === rawAccel.toUpperCase();
    if (isUpper) idx = label.indexOf(rawAccel);
    if (idx < 0) idx = label.toLowerCase().indexOf(rawAccel.toLowerCase());
  }
  if (idx < 0) return <span className="mp-btn__label">{label}</span>;
  return (
    <span className="mp-btn__label">
      {label.slice(0, idx)}
      <span className="mp-btn__accel">{label.slice(idx, idx + 1)}</span>
      {label.slice(idx + 1)}
    </span>
  );
}

/**
 * A small, accessible, native-feeling modal dialog primitive (reused for the
 * conflict dialog and future prompts). Renders an overlay + centered panel via
 * a portal so it escapes overflow/transform ancestors. Handles: focus trap
 * (Tab/Shift+Tab cycle), Esc-to-close, backdrop click, focus-into on open and
 * focus-restore on close. Fully guarded for SSR/jsdom (no crash when `document`
 * is unavailable or layout is absent).
 *
 * When given structured {@link ModalProps.actions}, the Modal also renders the
 * footer buttons and wires single-key accelerators, Enter-as-default and the
 * Esc-as-cancel behavior; otherwise it falls back to the `footer` prop and
 * behaves exactly as before.
 *
 * Keyboard isolation: any key this modal *consumes* (Esc, Enter-as-default, a
 * matching letter accelerator, a focus-trap-wrapping Tab) calls
 * `stopPropagation()` so it can never reach an ancestor modal's `onKeyDown`.
 * This is what keeps stacked modals from double-handling a key even when a
 * `<Modal>` is rendered *inside* another Modal's `children` (React routes
 * portal events along the component tree, so DOM-sibling rendering alone is no
 * longer the only thing preventing a double-fire).
 *
 * The app's global `window` keydown shortcuts (command palette, new/close tab,
 * split, view-switch) are unaffected — not because of the portal boundary, but
 * because they are all modifier-gated (Ctrl/Meta) while the keys this modal
 * consumes are bare (Esc, Enter, un-modified letters, Tab), so the key spaces
 * are disjoint.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  actions,
  closeOnBackdrop = true,
  className,
}: ModalProps): ReactElement | null {
  const panelRef = useRef<HTMLDivElement | null>(null);
  // The element focused before we opened, restored on close/unmount.
  const restoreFocus = useRef<Element | null>(null);
  const titleId = useId();

  const hasActions = actions != null && actions.length > 0;
  const defaultAction = hasActions ? actions.find((a) => a.isDefault) : undefined;
  const cancelAction = hasActions ? actions.find((a) => a.isCancel) : undefined;

  // Move focus into the dialog on open; restore it to the previously-focused
  // element when it closes/unmounts. With actions, prefer the default action's
  // button so Enter feels natural and the visible default is sensible.
  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    restoreFocus.current = document.activeElement;
    const panel = panelRef.current;
    const preferred = panel?.querySelector<HTMLElement>('[data-default="true"]');
    const first = preferred ?? panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus();
    return () => {
      const el = restoreFocus.current;
      if (el instanceof HTMLElement && document.contains(el)) el.focus();
    };
  }, [open]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>): void => {
      // Whenever this modal *consumes* a key (acts on it), we also
      // `stopPropagation()` so the synthetic event can't reach an ancestor
      // modal's `onKeyDown`. React routes portal events along the component
      // tree, so a <Modal> rendered inside another Modal's `children` would
      // otherwise let a consumed key double-fire on the outer modal too. We
      // only stop at the consume points below — a key this modal ignores
      // (non-matching letter, Enter with no default, a Tab away from a
      // boundary) is left to propagate normally.
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        // With actions, Esc fires the cancel action if there is one; otherwise
        // the existing onClose default.
        if (cancelAction) cancelAction.onSelect();
        else onClose();
        return;
      }

      if (hasActions && actions) {
        // Enter triggers the default action (unless typing).
        if (e.key === 'Enter') {
          if (defaultAction && !isTypingTarget()) {
            e.preventDefault();
            e.stopPropagation();
            defaultAction.onSelect();
          }
          return;
        }
        // A bare single printable letter triggers the matching accelerator.
        if (
          e.key.length === 1 &&
          !e.ctrlKey &&
          !e.metaKey &&
          !e.altKey &&
          !isTypingTarget()
        ) {
          const pressed = e.key.toLowerCase();
          const match = actions.find((a) => acceleratorOf(a) === pressed);
          if (match) {
            e.preventDefault();
            e.stopPropagation();
            match.onSelect();
            return;
          }
        }
      }

      if (e.key !== 'Tab') return;
      // Focus trap: keep Tab/Shift+Tab cycling within the dialog.
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeEl = typeof document !== 'undefined' ? document.activeElement : null;
      if (e.shiftKey) {
        if (activeEl === first || activeEl === panel) {
          e.preventDefault();
          e.stopPropagation();
          last.focus();
        }
      } else if (activeEl === last) {
        e.preventDefault();
        e.stopPropagation();
        first.focus();
      }
    },
    [onClose, hasActions, actions, defaultAction, cancelAction],
  );

  if (!open || typeof document === 'undefined') return null;

  const renderedFooter = hasActions ? (
    <div className="mp-modal__footer">
      {actions.map((action, i) => {
        const accel = acceleratorOf(action);
        const variantClass =
          action.variant === 'primary'
            ? ' mp-btn--primary'
            : action.variant === 'danger'
              ? ' mp-btn--danger'
              : '';
        // Advertise the key(s) this button answers: its letter, plus Enter/Esc
        // when it is the default/cancel action.
        const shortcuts = [
          accel || null,
          action.isDefault ? 'Enter' : null,
          action.isCancel ? 'Escape' : null,
        ].filter(Boolean);
        return (
          <button
            key={action.id ?? `${action.label}:${i}`}
            type="button"
            className={`mp-btn${variantClass}`}
            data-default={action.isDefault ? 'true' : undefined}
            aria-keyshortcuts={shortcuts.length > 0 ? shortcuts.join(' ') : undefined}
            onClick={action.onSelect}
          >
            {labelWithAccel(action.label, action.accelerator ?? action.label.charAt(0))}
            {action.isDefault && (
              <span className="mp-btn__key" aria-hidden>
                {keyLabel('Enter')}
              </span>
            )}
            {action.isCancel && (
              <span className="mp-btn__key" aria-hidden>
                {keyLabel('Escape')}
              </span>
            )}
          </button>
        );
      })}
    </div>
  ) : footer ? (
    <div className="mp-modal__footer">{footer}</div>
  ) : null;

  const dialog = (
    <div
      className="mp-modal__backdrop"
      onMouseDown={(e) => {
        // Only dismiss when the press starts on the backdrop itself (not a
        // drag that ends there from inside the panel).
        if (closeOnBackdrop && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className={className ? `mp-modal ${className}` : 'mp-modal'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        {title && (
          <h2 id={titleId} className="mp-modal__title">
            {title}
          </h2>
        )}
        <div className="mp-modal__body">{children}</div>
        {renderedFooter}
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
}
